import parse from 'html-react-parser';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Edit2, Eye, RefreshCw, User, Clock, Pencil, CheckCircle2,
    AlertTriangle, Box, Calendar, ChevronDown, ChevronRight, Search, X,
} from 'lucide-react';
import { capitalize } from '../../../../components/tools/utils.jsx';
import { editorPathForModel } from '../../../../products/registry';
import ProductTypeBadge from '../../../../products/ProductTypeBadge.jsx';
import ARViewer from '../../../Customiz3r/AR/ARViewer.jsx';

function removeWidthAndHeight(svgString) {
    return svgString
        .replace(/width="[^"]*"/, '')
        .replace(/height="[^"]*"/, '');
}

function GeometryBadge({ stats }) {
    if (!stats) return (
        <span className="badge badge-ghost badge-sm gap-1 opacity-50">
            <Box className="w-3 h-3" /> Sin análisis
        </span>
    );
    if (stats.is_watertight) return (
        <span className="badge badge-success badge-sm gap-1">
            <CheckCircle2 className="w-3 h-3" /> Watertight
        </span>
    );
    return (
        <span className="badge badge-warning badge-sm gap-1" title={`Caras abiertas: ${stats.open_faces}`}>
            <AlertTriangle className="w-3 h-3" /> {stats.open_faces} abiertas
        </span>
    );
}

function ChangeOwnerPopover({ modeloId, onUpdated, onClose }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    useEffect(() => {
        if (!query.trim()) { setResults([]); return; }
        const id = setTimeout(async () => {
            setSearching(true);
            try {
                const { data } = await axios.get('users-admin/', { params: { search: query, page_size: 6 } });
                setResults(Array.isArray(data) ? data : (data.results ?? []));
            } catch { setResults([]); }
            finally { setSearching(false); }
        }, 300);
        return () => clearTimeout(id);
    }, [query]);

    const handleSelect = async (user) => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.patch(`customized-keyrings-admin/${modeloId}/`, { usuario: user.id });
            onUpdated(data);
            onClose();
        } catch {
            setError('Error al cambiar propietario');
            setSaving(false);
        }
    };

    return (
        <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-base-100 border border-base-300
                        rounded-xl shadow-xl p-3 flex flex-col gap-2"
             onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-base-content/70">Cambiar propietario</span>
                <button className="btn btn-ghost btn-xs btn-square" onClick={onClose}><X className="w-3 h-3" /></button>
            </div>
            <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-base-content/40" />
                <input
                    ref={inputRef}
                    type="text"
                    className="input input-sm input-bordered w-full pl-7"
                    placeholder="Buscar por email o nombre..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Escape' && onClose()}
                />
                {searching && <span className="absolute right-2 top-1/2 -translate-y-1/2 loading loading-spinner loading-xs" />}
            </div>
            {results.length > 0 && (
                <ul className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                    {results.map(u => (
                        <li key={u.id}>
                            <button
                                className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-base-200 transition-colors
                                           flex flex-col gap-0 disabled:opacity-50"
                                onClick={() => handleSelect(u)}
                                disabled={saving}
                            >
                                <span className="text-xs font-medium text-base-content truncate">
                                    {u.name || u.public_username || '—'}
                                </span>
                                <span className="text-xs text-base-content/50 truncate">{u.email}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {query && !searching && results.length === 0 && (
                <p className="text-xs text-base-content/40 text-center py-2">Sin resultados</p>
            )}
            {error && <p className="text-xs text-error">{error}</p>}
            {saving && <p className="text-xs text-base-content/50 text-center">Guardando...</p>}
        </div>
    );
}

function ChangeOwnerInline({ modeloId, onUpdated }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={containerRef}>
            <button
                className="btn btn-ghost btn-xs gap-1 text-base-content/40 hover:text-primary p-0 h-auto min-h-0"
                onClick={() => setOpen(v => !v)}
                title="Cambiar propietario"
            >
                <Pencil className="w-3 h-3" />
            </button>
            {open && (
                <ChangeOwnerPopover
                    modeloId={modeloId}
                    onUpdated={onUpdated}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
}

function Modelo3dCardListAdmin({ modelo: initialModelo, onDelete }) {
    const navigate = useNavigate();
    const [modelo, setModelo] = useState(initialModelo);
    const [regenerating, setRegenerating] = useState(false);
    const [regenError, setRegenError] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const handleRegenerate = async () => {
        setRegenerating(true);
        setRegenError(null);
        try {
            const { data } = await axios.post(`customized-keyrings-admin/${modelo.id}/regenerate-3d/`);
            setModelo(prev => ({
                ...prev,
                modified_at: data.generated_at,
                geometry_stats: data.geometry_stats,
                has_3d: true,
            }));
        } catch {
            setRegenError('Error al regenerar');
        } finally {
            setRegenerating(false);
        }
    };

    const dims = modelo.form2d_data || {};
    const width = dims.width ?? dims.baseLayout?.width;
    const height = dims.height ?? dims.baseLayout?.height;
    const thickness = dims.thicknessModelo3d ?? dims.thickness;

    return (
        <div className="group bg-base-100 rounded-xl shadow-sm border border-base-200
                        hover:shadow-md hover:border-primary/20 transition-all duration-200 overflow-visible">
            {/* Main row */}
            <div className="flex items-stretch h-24">
                {/* Accent stripe */}
                <div className="w-1 shrink-0 bg-primary rounded-l-xl" />

                {/* Thumbnail */}
                <div className="relative w-24 h-full shrink-0 svg-checker flex items-center justify-center p-2 overflow-hidden">
                    <div className={`w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain transition-opacity ${modelo.path_thumbnail_3d ? 'group-hover:opacity-0' : ''}`}>
                        {modelo.svg_2d && parse(
                            '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' +
                            removeWidthAndHeight(modelo.svg_2d)
                        )}
                    </div>
                    {modelo.path_thumbnail_3d && (
                        <img
                            src={modelo.path_thumbnail_3d}
                            alt={`Render 3D de ${modelo.name}`}
                            className="absolute inset-0 h-full w-full object-contain bg-base-200 p-2
                                       opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        />
                    )}
                </div>

                {/* Divider */}
                <div className="w-px bg-base-200 shrink-0 my-2" />

                {/* Main info */}
                <div className="flex-1 min-w-0 flex flex-col justify-center px-3 gap-0.5">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base-content text-sm leading-snug truncate"
                            title={capitalize(modelo?.name)}>
                            {capitalize(modelo?.name)}
                        </h3>
                        <ProductTypeBadge modelo={modelo} compact className="shrink-0" />
                        <span className="shrink-0">
                            {modelo.has_3d
                                ? <span className="badge badge-primary badge-xs">3D</span>
                                : <span className="badge badge-ghost badge-xs opacity-50">Sin 3D</span>
                            }
                        </span>
                    </div>

                    {/* Creator row */}
                    <div className="relative flex items-center gap-1 text-xs text-base-content/50">
                        <User className="w-3 h-3 shrink-0" />
                        <span className="truncate max-w-[120px]" title={modelo.usuario_email}>
                            {modelo.usuario_username || modelo.usuario_email}
                        </span>
                        <ChangeOwnerInline
                            modeloId={modelo.id}
                            onUpdated={updated => setModelo(prev => ({ ...prev, ...updated }))}
                        />
                    </div>

                    {/* Dates row */}
                    <div className="flex items-center gap-3 text-xs text-base-content/40">
                        <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 shrink-0" />
                            {modelo.created_at
                                ? new Date(modelo.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                                : '—'}
                        </span>
                        {modelo.modified_at && modelo.modified_at !== modelo.created_at && (
                            <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3 shrink-0" />
                                {new Date(modelo.modified_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                        )}
                    </div>
                </div>

                {/* Stats column */}
                <div className="hidden lg:flex flex-col justify-center gap-1 px-3 shrink-0 border-l border-base-200 min-w-[140px]">
                    <GeometryBadge stats={modelo.geometry_stats} />
                    {(width || height) && (
                        <span className="text-xs text-base-content/50 flex items-center gap-1">
                            <Box className="w-3 h-3 shrink-0" />
                            {width && height ? `${width}×${height}mm` : width ? `${width}mm` : `${height}mm`}
                            {thickness ? ` · ${thickness}mm` : ''}
                        </span>
                    )}
                    {modelo.geometry_stats && (
                        <span className="text-xs text-base-content/40">
                            {modelo.geometry_stats.total_faces?.toLocaleString('es-ES')} caras
                        </span>
                    )}
                </div>

                {/* Actions column */}
                <div className="flex flex-col justify-center gap-1 px-2 shrink-0">
                    <button
                        className="btn btn-ghost btn-xs btn-square text-base-content/60 hover:text-primary hover:bg-primary/10"
                        onClick={() => navigate(editorPathForModel(modelo))}
                        title="Editar"
                    >
                        <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                        className="btn btn-ghost btn-xs btn-square text-base-content/60 hover:text-secondary hover:bg-secondary/10"
                        onClick={() => navigate(`/viewer/${modelo.id}/`)}
                        title="Ver en 3D"
                    >
                        <Eye className="w-3.5 h-3.5" />
                    </button>
                    {modelo.has_3d !== false && (
                        <ARViewer
                            modelId={modelo.id}
                            revision={modelo.glb_revision || modelo.viewer_revision || modelo.revision || 1}
                            compact
                            inline
                            direct
                        />
                    )}
                    <button
                        className={`btn btn-ghost btn-xs btn-square hover:bg-accent/10 ${regenerating ? 'text-accent' : 'text-base-content/60 hover:text-accent'}`}
                        onClick={handleRegenerate}
                        disabled={regenerating}
                        title="Regenerar modelo 3D"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        className="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-base-content"
                        onClick={() => setExpanded(v => !v)}
                        title="Ver más datos"
                    >
                        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>

            {/* Expandable details */}
            {expanded && (
                <div className="border-t border-base-200 px-4 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                    <div>
                        <p className="text-base-content/40 uppercase tracking-wide mb-0.5">ID</p>
                        <p className="font-mono text-base-content/70">#{modelo.id}</p>
                    </div>
                    <div>
                        <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Propietario</p>
                        <p className="text-base-content/70 truncate">{modelo.usuario_name || '—'}</p>
                        <p className="text-base-content/40 truncate">{modelo.usuario_email}</p>
                    </div>
                    <div>
                        <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Visibilidad</p>
                        <p className="text-base-content/70">{modelo.sharing_permissions || '—'}</p>
                    </div>
                    <div>
                        <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Dimensiones</p>
                        <p className="text-base-content/70">
                            {width && height ? `${width} × ${height} mm` : '—'}
                            {thickness ? ` (${thickness}mm grosor)` : ''}
                        </p>
                    </div>
                    {modelo.geometry_stats && (
                        <>
                            <div>
                                <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Caras abiertas</p>
                                <p className={modelo.geometry_stats.open_faces > 0 ? 'text-warning font-medium' : 'text-success'}>
                                    {modelo.geometry_stats.open_faces}
                                </p>
                            </div>
                            <div>
                                <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Total caras</p>
                                <p className="text-base-content/70">{modelo.geometry_stats.total_faces?.toLocaleString('es-ES')}</p>
                            </div>
                            <div>
                                <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Vértices</p>
                                <p className="text-base-content/70">{modelo.geometry_stats.total_vertices?.toLocaleString('es-ES')}</p>
                            </div>
                        </>
                    )}
                    <div>
                        <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Creado</p>
                        <p className="text-base-content/70">
                            {modelo.created_at ? new Date(modelo.created_at).toLocaleString('es-ES') : '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-base-content/40 uppercase tracking-wide mb-0.5">Modificado</p>
                        <p className="text-base-content/70">
                            {modelo.modified_at ? new Date(modelo.modified_at).toLocaleString('es-ES') : '—'}
                        </p>
                    </div>
                    {regenError && (
                        <div className="col-span-full">
                            <p className="text-error text-xs">{regenError}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Modelo3dCardListAdmin;
